import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import api from '../api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) return;
    
    setRecoveryLoading(true);
    try {
      await api.post('/api/auth/recuperar-password', { email: recoveryEmail });
      toast({
        title: 'Email enviado',
        description: 'Si el email existe, recibirás instrucciones para restablecer tu contraseña.',
      });
      setShowRecovery(false);
      setRecoveryEmail('');
    } catch (error) {
      toast({
        title: 'Solicitud procesada',
        description: 'Si el email existe en el sistema, recibirás instrucciones.',
      });
      setShowRecovery(false);
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        toast({
          title: '¡Bienvenido!',
          description: 'Has iniciado sesión correctamente',
        });
        navigate('/panel');
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Credenciales inválidas',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Ha ocurrido un error. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex flex-col">
      {/* Header */}
      <div className="bg-blue-700 text-white py-3 px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-white rounded flex items-center justify-center">
            <span className="text-blue-700 font-bold text-sm">L</span>
          </div>
          <span className="font-semibold text-lg">LEXDUO</span>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <a href="https://www.lexduo.cat" className="hover:underline">www.lexduo.cat</a>
          <span>|</span>
          <a href="mailto:info@lexduo.cat" className="hover:underline">info@lexduo.cat</a>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-2xl">L</span>
              </div>
              <span className="text-4xl font-bold text-blue-900">LEXDUO</span>
            </div>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Iniciar sesión</h2>
            
            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-blue-700 hover:bg-blue-800" 
                disabled={loading}
              >
                {loading ? 'Accediendo...' : 'Iniciar sesión'}
              </Button>
              
              <div className="text-center mt-4">
                <button 
                  type="button"
                  onClick={() => setShowRecovery(true)}
                  className="text-sm text-blue-700 hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t py-4 text-center text-sm text-gray-600">
        <div>2025 © LEXDUO ADVOCATS SLP - Todos los derechos reservados</div>
        <div className="text-xs text-gray-400 mt-1">Versión 1.4.0</div>
      </div>

      {/* Dialog para recuperar contraseña */}
      <Dialog open={showRecovery} onOpenChange={setShowRecovery}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recuperar contraseña</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecoverySubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              Introduce tu email y te enviaremos instrucciones para restablecer tu contraseña.
            </p>
            <div>
              <Label htmlFor="recovery-email">Email</Label>
              <Input
                id="recovery-email"
                type="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                placeholder="tu@email.com"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRecovery(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={recoveryLoading}>
                {recoveryLoading ? 'Enviando...' : 'Enviar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
